import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import PerformanceWidget from './components/PerformanceWidget';
import KnowledgeAreaCard from './components/KnowledgeAreaCard';
import RecentActivityItem from './components/RecentActivityItem';
import MetricCard from './components/MetricCard';
import StudyGoalCard from './components/StudyGoalCard';
import PerformanceChart from './components/PerformanceChart';
import WeakAreaAlert from './components/WeakAreaAlert';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentDate] = useState(new Date('2026-02-05T09:55:13'));

  const performanceData = {
    totalQuestions: 120000,
    questionsAttempted: 8450,
    averageScore: 78,
    studyStreak: 12,
    totalTests: 34,
    recentTestScore: 82
  };

  const knowledgeAreas = [
    {
      area: "Business Analysis Planning and Monitoring",
      questionsAttempted: 1250,
      totalQuestions: 15000,
      averageScore: 82,
      lastPracticed: "2 days ago"
    },
    {
      area: "Elicitation and Collaboration",
      questionsAttempted: 1580,
      totalQuestions: 18000,
      averageScore: 75,
      lastPracticed: "1 day ago"
    },
    {
      area: "Requirements Life Cycle Management",
      questionsAttempted: 1420,
      totalQuestions: 16500,
      averageScore: 88,
      lastPracticed: "3 days ago"
    },
    {
      area: "Strategy Analysis",
      questionsAttempted: 980,
      totalQuestions: 14000,
      averageScore: 68,
      lastPracticed: "5 days ago"
    },
    {
      area: "Requirements Analysis and Design Definition",
      questionsAttempted: 1650,
      totalQuestions: 19000,
      averageScore: 79,
      lastPracticed: "Today"
    },
    {
      area: "Solution Evaluation",
      questionsAttempted: 1570,
      totalQuestions: 17500,
      averageScore: 71,
      lastPracticed: "4 days ago"
    }
  ];

  const recentActivities = [
    {
      type: "test",
      title: "Full Practice Test - CBAP Simulation",
      score: 82,
      questionsCount: 150,
      timestamp: "2 hours ago",
      knowledgeArea: "All Areas"
    },
    {
      type: "practice",
      title: "Requirements Analysis Focus Session",
      score: 88,
      questionsCount: 50,
      timestamp: "Yesterday",
      knowledgeArea: "Requirements Analysis"
    },
    {
      type: "milestone",
      title: "Completed 8,000 Questions Milestone",
      score: null,
      questionsCount: null,
      timestamp: "2 days ago",
      knowledgeArea: null
    },
    {
      type: "test",
      title: "Strategy Analysis Quick Quiz",
      score: 65,
      questionsCount: 20,
      timestamp: "3 days ago",
      knowledgeArea: "Strategy Analysis"
    },
    {
      type: "bookmark",
      title: "Bookmarked 15 Complex Scenario Questions",
      score: null,
      questionsCount: 15,
      timestamp: "4 days ago",
      knowledgeArea: "Elicitation"
    }
  ];

  const studyGoals = [
    {
      goal: "Complete 10,000 practice questions",
      current: 8450,
      target: 10000,
      dueDate: "02/28/2026",
      priority: "high"
    },
    {
      goal: "Achieve 85% average in all knowledge areas",
      current: 4,
      target: 6,
      dueDate: "03/15/2026",
      priority: "medium"
    },
    {
      goal: "Complete 50 full practice tests",
      current: 34,
      target: 50,
      dueDate: "03/31/2026",
      priority: "medium"
    },
    {
      goal: "Master Strategy Analysis domain",
      current: 68,
      target: 85,
      dueDate: "02/20/2026",
      priority: "high"
    }
  ];

  const chartData = [
    { name: "Planning", score: 82, questions: 1250 },
    { name: "Elicitation", score: 75, questions: 1580 },
    { name: "Lifecycle", score: 88, questions: 1420 },
    { name: "Strategy", score: 68, questions: 980 },
    { name: "Analysis", score: 79, questions: 1650 },
    { name: "Evaluation", score: 71, questions: 1570 }
  ];

  const weakAreas = [
    { name: "Strategy Analysis", score: 68, target: 85 },
    { name: "Solution Evaluation", score: 71, target: 85 },
    { name: "Elicitation and Collaboration", score: 75, target: 85 }
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard - BABOK Master Pro</title>
        <meta name="description" content="Track your IIBA-CBAP exam preparation progress with comprehensive analytics and performance insights" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-[60px]">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            {/* Welcome Section */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Welcome back to your CBAP Journey
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {currentDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            {/* Overall Progress */}
            <div className="mb-6 md:mb-8">
              <ProgressIndicator
                title="Overall Exam Preparation Progress"
                current={performanceData?.questionsAttempted}
                total={performanceData?.totalQuestions}
                showDetails={true}
                className="animate-fade-in"
              />
            </div>

            {/* Performance Widgets */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              <PerformanceWidget
                title="Questions Attempted"
                value={performanceData?.questionsAttempted}
                total={performanceData?.totalQuestions}
                icon="HelpCircle"
                iconBg="bg-primary"
                trend="up"
                trendValue="+450 this week"
              />
              <PerformanceWidget
                title="Practice Tests Completed"
                value={performanceData?.totalTests}
                total={50}
                icon="FileText"
                iconBg="bg-secondary"
                trend="up"
                trendValue="+3 this week"
              />
              <PerformanceWidget
                title="Average Score"
                value={performanceData?.averageScore}
                total={100}
                icon="Target"
                iconBg="bg-success"
                trend="up"
                trendValue="+5% improvement"
              />
            </div>

            {/* Quick Actions */}
            <div className="mb-6 md:mb-8">
              <QuickActionPanel />
            </div>

            {/* Weak Areas Alert */}
            {weakAreas?.length > 0 && (
              <div className="mb-6 md:mb-8">
                <WeakAreaAlert areas={weakAreas} />
              </div>
            )}

            {/* Summary Metrics */}
            <div className="mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                Key Statistics
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Study Streak"
                  value={`${performanceData?.studyStreak} days`}
                  subtitle="Keep it going!"
                  icon="Flame"
                  iconBg="bg-accent"
                  trend={{ direction: 'up', value: '+2 days' }}
                />
                <MetricCard
                  title="Recent Test Score"
                  value={`${performanceData?.recentTestScore}%`}
                  subtitle="Latest performance"
                  icon="TrendingUp"
                  iconBg="bg-success"
                  trend={{ direction: 'up', value: '+7%' }}
                />
                <MetricCard
                  title="Bookmarked Questions"
                  value="127"
                  subtitle="For review"
                  icon="Bookmark"
                  iconBg="bg-warning"
                  trend={{ direction: 'neutral', value: '' }}
                />
                <MetricCard
                  title="Study Hours"
                  value="48.5"
                  subtitle="This month"
                  icon="Clock"
                  iconBg="bg-primary"
                  trend={{ direction: 'up', value: '+12h' }}
                />
              </div>
            </div>

            {/* Knowledge Area Performance */}
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                  Knowledge Area Performance
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/results-analytics')}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  View Details
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {knowledgeAreas?.map((area, index) => (
                  <KnowledgeAreaCard
                    key={index}
                    area={area?.area}
                    questionsAttempted={area?.questionsAttempted}
                    totalQuestions={area?.totalQuestions}
                    averageScore={area?.averageScore}
                    lastPracticed={area?.lastPracticed}
                  />
                ))}
              </div>
            </div>

            {/* Performance Chart */}
            <div className="mb-6 md:mb-8">
              <PerformanceChart
                data={chartData}
                title="Knowledge Area Score Comparison"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {/* Recent Activity */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                    Recent Activity
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/history')}
                    iconName="History"
                    iconPosition="left"
                  >
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {recentActivities?.map((activity, index) => (
                    <RecentActivityItem
                      key={index}
                      type={activity?.type}
                      title={activity?.title}
                      score={activity?.score}
                      questionsCount={activity?.questionsCount}
                      timestamp={activity?.timestamp}
                      knowledgeArea={activity?.knowledgeArea}
                    />
                  ))}
                </div>
              </div>

              {/* Study Goals */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                    Study Goals
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/profile')}
                    iconName="Settings"
                    iconPosition="left"
                  >
                    Manage
                  </Button>
                </div>
                <div className="space-y-3">
                  {studyGoals?.map((goal, index) => (
                    <StudyGoalCard
                      key={index}
                      goal={goal?.goal}
                      current={goal?.current}
                      target={goal?.target}
                      dueDate={goal?.dueDate}
                      priority={goal?.priority}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-8 md:mt-10 bg-gradient-to-r from-primary to-secondary rounded-xl p-6 md:p-8 text-center">
              <div className="max-w-2xl mx-auto">
                <Icon name="Target" size={48} color="white" className="mx-auto mb-4" />
                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-white mb-3">
                  Ready to Continue Your Preparation?
                </h2>
                <p className="text-sm md:text-base text-white/90 mb-6">
                  You're making great progress! Keep practicing to achieve your CBAP certification goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => navigate('/question-practice?mode=full&questions=150')}
                    iconName="Play"
                    iconPosition="left"
                    className="bg-white text-primary hover:bg-white/90"
                  >
                    Start Full Practice Test
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate('/resources')}
                    iconName="BookOpen"
                    iconPosition="left"
                    className="border-white text-white hover:bg-white/10"
                  >
                    Browse Study Materials
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;